<template>
  <ValidationObserver ref="registerFormValidator" v-slot="{ errors }">
    <div v-if="useGoogleSSO" class="mt-7">
      <GoogleSignInHblButton expanded type="button" @click.native="ssoSubmit(googleProviderName)">{{ $t('registerForm_googleSso_buttonText') }}</GoogleSignInHblButton>
    </div>
    <p v-if="useGoogleSSO" class="por--divider z-u-text-body-bold mt-7">{{ $t('registerForm_divider_text') }}</p>
    <form class="mt-7"
      @submit.prevent="submitRegister"
      novalidate
      data-qa="register-form">

      <!-- # Given Name -->
      <Input
        id="given_name"
        :input-model.sync="given_name"
        validation-rules="required|valid_name"
        :label-text="$t('registerForm_firstName_input')"
        :placeholder-text="$t('registerForm_placeholderFirstName_text')"
        input-type="text"
        :error-text="`${$t('registerForm_emptyFirstName_text')}.`"
        class="por-register--field-wrapper"
        data-qa="register-form--given_name" />

      <div class="is-size-7 por-register--field-helper-text">
        <span data-qa="register-form--email-bottom-info">{{ $t('registerForm_firstNameBottom_text') }}</span>
      </div>

      <!-- # Family Name -->
      <Input
        id="family_name"
        :input-model.sync="family_name"
        validation-rules="required|valid_name"
        :label-text="$t('registerForm_lastName_input')"
        :placeholder-text="$t('registerForm_placeholderLastName_text')"
        input-type="text"
        :error-text="`${$t('registerForm_emptyLastName_text')}.`"
        class="por-register--field-wrapper mt-4"
        data-qa="register-form--family_name" />

      <div class="is-size-7 por-register--field-helper-text">
        <span data-qa="register-form--email-bottom-info">{{ $t('registerForm_lastNameBottom_text') }}</span>
      </div>

      <!-- # Email -->
      <Input
        id="email"
        :input-model.sync="email"
        validation-rules="required|email"
        :label-text="$t('common_email_label')"
        :placeholder-text="$t('common_email_placeholder')"
        input-type="email"
        :error-text="`${$t('common_validEmail_text')}.`"
        class="por-register--field-wrapper mt-4"
        data-qa="register-form--email" />

      <div class="is-size-7 por-register--field-helper-text">
        <span data-qa="register-form--email-bottom-info">{{ $t('registerForm_verifyEmail_text') }}</span>
      </div>

      <!-- # Password -->
      <Password
        id="password"
        :input-model.sync="password"
        :label-text="$t('common_password_text')"
        placeholder-text="••••••••"
        :error-text="`${$t('common_validPassword_text')}.`"
        class="por-register--field-wrapper mt-4"
        data-qa="register-form--password" />

      <!-- # Birthday -->
      <div class="por-register--field-wrapper mb-4">
        <div class="Row">
          <label
            class="label z-u-text-label"
            for="birthdate"
            data-qa="form-birthdate--label">
            {{ $t('registerForm_dob_label') }}
          </label>
          <div class="columns columns-no-margin">
            <div class="column column-no-padding" data-qa="register-form--birthdate_day">
              <ValidationProvider name="birthdate_day" rules="required" v-slot="{ errors }">
                <b-field
                  custom-class="z-u-text-label"
                  :type="{ 'is-danger': errors[0] }"
                  class="m-0">
                  <b-select
                    v-model="birthdate_day"
                    :aria-label="$t('registerForm_day_label')"
                    :placeholder="$t('registerForm_day_label')"
                    id="form-birthdate--input-day"
                    name="form-birthdate--input-day"
                    size="is-medium"
                    data-qa="form-birthdate--input"
                    expanded
                    data-hj-suppress>
                    <option
                      v-for="day in getDaysInMonth()"
                      :key="`day-${day.number}`"
                      :value="day.number">
                      {{ day.value }}
                    </option>
                  </b-select>
                </b-field>
              </ValidationProvider>
            </div>
            <div class="column column-no-padding" data-qa="register-form--birthdate_month">
              <ValidationProvider name="birthdate_month" rules="required" v-slot="{ errors }">
                <b-field
                  custom-class="z-u-text-label"
                  :type="{ 'is-danger': errors[0] }"
                  class="m-0">
                  <b-select
                    v-model="birthdate_month"
                    :aria-label="$t('registerForm_month_select')"
                    :placeholder="$t('registerForm_month_select')"
                    id="form-birthdate--input-month"
                    name="form-birthdate--input-month"
                    size="is-medium"
                    data-qa="form-birthdate--input"
                    expanded
                    data-hj-suppress>
                    <option
                      v-for="month in getMonthsInYear()"
                      :key="`month-${month.number}`"
                      :value="month.number">
                      {{ $t(month.value) }}
                    </option>
                  </b-select>
                </b-field>
              </ValidationProvider>
            </div>
            <div class="column column-no-padding" data-qa="register-form--birthdate_year">
              <ValidationProvider name="birthdate_year" rules="required" v-slot="{ errors }">
                <b-field
                  custom-class="z-u-text-label"
                  :type="{ 'is-danger': errors[0] }"
                  class="m-0">
                  <b-select
                    v-model="birthdate_year"
                    :aria-label="$t('registerForm_year_select')"
                    :placeholder="$t('registerForm_year_select')"
                    id="form-birthdate--input-year"
                    name="form-birthdate--input-year"
                    size="is-medium"
                    data-qa="form-birthdate--input"
                    expanded
                    data-hj-suppress>
                    <option
                      v-for="year in getYears()"
                      :key="`year-${year}`"
                      :value="year.toString()">
                      {{ year }}
                  </option>
                  </b-select>
                </b-field>
              </ValidationProvider>
            </div>
          </div>
          <!-- The below 'errors' is coming from the form ValidationObserver, NOT the ValidationProvider -->
          <span
            class="por-input-error-text"
            data-qa="form-birthdate_day--error"
            v-if="
              errors && errors['birthdate_day'] && !!errors['birthdate_day'].length ||
              errors && errors['birthdate_month'] && !!errors['birthdate_month'].length ||
              errors && errors['birthdate_year'] && !!errors['birthdate_year'].length
            ">
              {{ $t('registerForm_validDate_text') }}.
            </span>
        </div>
      </div>

      <div class="is-size-7 por-register--field-helper-text">
        <span data-qa="register-form--email-bottom-info">{{ $t('registerForm_dateOfBirthBottom_text') }}</span>
      </div>

      <!-- # Gender -->
      <fieldset
        class="por-register--field-wrapper mt-4"
        data-qa="form-gender--wrapper">
        <ValidationProvider name="gender" rules="required" v-slot="{ errors }">
          <div>
            <legend
              class="label z-u-text-label"
              data-qa="form-gender--legend">
              {{ $t('registerForm_gender_legend') }}
            </legend>
            <div class="columns columns-no-margin">
              <div class="column column-no-padding">
                <b-radio
                  :class="{ 'por-input-field--error': !!errors.length }"
                  class="por-register-input--wrapper-input pt-2 pr-2 pb-2 pl-2"
                  v-model="gender"
                  id="gender_female"
                  native-value="f"
                  data-qa="form-gender--input"
                  data-hj-suppress>
                  <span data-qa="form-gender--label">
                    {{ $t('registerForm_genderFemale_radio') }}
                  </span>
                </b-radio>
              </div>
              <div class="column column-no-padding">
                <b-radio
                  :class="{ 'por-input-field--error': !!errors.length }"
                  class="por-register-input--wrapper-input pt-2 pr-2 pb-2 pl-2"
                  v-model="gender"
                  id="gender_male"
                  native-value="m"
                  data-qa="form-gender--input"
                  data-hj-suppress>
                  <span data-qa="form-gender--label">
                    {{ $t('registerForm_genderMale_label') }}
                  </span>
                </b-radio>
              </div>
            </div>
            <span
              v-if="!!errors.length"
              class="por-input-error-text"
              data-qa="form-gender--error">
              {{ $t('registerForm_emptyOptions_text') }}.
            </span>
          </div>
        </ValidationProvider>
      </fieldset>

      <div v-if="isSuperdrug" class="por-register--sd-wrapper pt-6 pb-6" data-qa="por-register--sd-wrapper">
        <h3 class="z-u-text-label">Order communications</h3>
        <p class="por-register--sd-text">Superdrug Online Doctor will contact you via email for order related purposes. No medical information will be in these messages.</p>
      </div>

      <!-- # Phone -->
      <div data-qa="form-register--phone_number_wrapper">
        <PhoneInput
          :label-text="$t('registerForm_mobile_text')"
          id="phone"
          ref="phoneInput"
          v-model="phone_number"
          @isValid="updatePhoneValidity"
          class="por-register--field-wrapper"
          data-qa="form-register--phone_number"
          :key="phoneInputKey"
          :custom-error-message="true"
        >
          <template #helper-text>
            <span class="subtitle">{{ $t('registerForm_mobile_subtitle') }}</span>
          </template>
          <template v-if="!isPhoneValid" #error-info-text>
            <a data-qa="form-register--mobile_sms_clear" @click="clearPhoneInput('register')">{{ $t('registerForm_mobile_sms_clear') }}</a>
          </template>
        </PhoneInput>
      </div>

      <ValidationProvider
        name="sms_options_selection"
        :rules="{ required_if:phone_number.length > 0 }"
        v-slot="{ errors }">
        <!-- Hidden checkbox required as only first checkbox is used for validation -->
        <b-checkbox style="display: none;" :value="(sms_updates || sms_offers)" />
        <div
          v-if="displaySmsOptions"
          class="por-register--sms-modal mt-3 p-6"
          data-qa="form-register--sms-modal"
          :class="{ 'por-input-field--error': errors[0] }">
          <span class="por-register--sms-modal-header">{{ $t('registerForm_mobile_sms_heading')  }}</span>
          <b-checkbox
            class="por-register--sms-modal-input pt-2 pr-2 pb-2 pl-2 mt-4 mb-2"
            :class="{ 'por-input-field--error': errors[0] }"
            name="smsUpdates"
            id="smsUpdates"
            :value="false"
            v-model="sms_updates">
            <span data-qa="form-sms_updates--input">
              {{ $t('registerForm_mobile_sms_updates')  }}
            </span>
          </b-checkbox>
          <b-checkbox
            class="por-register--sms-modal-input pt-2 pr-2 pb-2 pl-2 mt-2"
            :class="{ 'por-input-field--error': errors[0] }"
            name="smsOffers"
            id="smsOffers"
            :value="false"
            v-model="sms_offers">
            <span data-qa="form-sms_offers--input">
              {{ $t('registerForm_mobile_sms_offers')  }}
            </span>
          </b-checkbox>
          <span
            v-if="!!errors.length"
            class="por-input-error-text"
            data-qa="form-register--sms-modal-error">
            {{ $t('registerForm_mobile_sms_consent_error') }} <a @click="clearPhoneInput('register')">{{ $t('registerForm_mobile_sms_clear') }}</a>
          </span>
        </div>
      </ValidationProvider>

      <div
        v-if="sms_cleared && phone_number.length < 1"
        class="por-register--sms-confirm p-4">
        <strong>{{ $t('registerForm_mobile_sms_removed') }}</strong>
      </div>

      <div class="por-register--optin-wrapper pt-6 pb-6" data-qa="por-register--optin-wrapper">
        <h3 class="z-u-text-label">{{ $t('registerForm_marketing_text') }}</h3>
        <p class="por-register--optin-text">{{ $t('registerForm_promotional_text') }}</p>
        <p class="por-register--optin-text pt-6">{{ $t('registerForm_optOut_text') }}</p>
      </div>

      <!-- # Third party -->
      <fieldset v-if="thirdParty" data-qa="por-register--third-party-field-wrapper"
        class="por-register--field-wrapper">
        <legend class="por-register--hidden" aria-hidden="false">
          {{ $t(`${thirdParty}_signupMarketing_text`) }}
        </legend>
        <div class="por-register--third-party-wrapper" data-qa="por-register--third-party-wrapper">
          <h3 class="z-u-text-label pb-1">{{ $t(`${thirdParty}_signupMarketing_text`) }}</h3>
          <b-field
            custom-class="z-u-text-label"
            :type="{ 'is-danger': errors[0] }">
            <b-checkbox
              class="por-register-input--wrapper-input pt-2 pr-2 pb-2 pl-2"
              name="thirdParty"
              id="thirdParty"
              v-model="third_party_consent"
              data-qa="form-third_party_consent--input">
              {{ $t(`${thirdParty}_receiveMarketingMails_text`) }}
            </b-checkbox>
          </b-field>
        </div>
      </fieldset>

      <!-- # T&C -->
      <fieldset
        class="por-register--field-wrapper"
        data-qa="form-terms_and_conditions--wrapper">
        <legend class="por-register--hidden" aria-hidden="false">
          {{ $t('registerForm_termsConditions_text') }}
        </legend>
        <ValidationProvider name="terms_and_conditions" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
          <b-field
            custom-class="z-u-text-label"
            :label="$t('registerForm_termsConditions_text')"
            :message="[errors[0] ? `${$t('registerForm_termsAndConditions_text')}.` : '']"
            :type="{ 'is-danger': errors[0] }"
            >
            <b-checkbox
              class="por-register-input--wrapper-input pt-2 pr-2 pb-2 pl-2"
              :class="{ 'por-input-field--error': errors[0] }"
              name="terms_and_conditions"
              id="terms_and_conditions"
              :value="true"
              v-model="terms_and_conditions"
              data-qa="form-terms_and_conditions--input">
              <span data-qa="form-terms_and_conditions--input">
                {{ $t('registerForm_agreeMessage_checkbox') }} <a :href="tcLink" target="_blank" rel="noopener noreferrer">{{ $t('registerForm_termsConditions_text') }}</a> {{ $t('registerForm_Formterms_text') }} <a :href="privacyLink" target="_blank" rel="noopener noreferrer">{{ $t('common_privacyPolicy_text') }}</a><span v-if="isDe"> zu</span>.
              </span>
            </b-checkbox>
          </b-field>
        </ValidationProvider>
      </fieldset>

      <!-- # Submit Button -->
      <div class="por-register--field-wrapper">
        <b-button
          size="is-medium"
          type="is-primary"
          expanded
          :disabled="isSubmitting"
          :loading="isSubmitting"
          tag="input"
          native-type="submit"
          :value="$t('common_register_text')"
          data-qa="form-submit-button" />
      </div>

      <!-- # Recaptcha -->
      <div class="por-register--recaptcha-wrapper">
        <VueRecaptcha
          ref="recaptcha"
          badge="inline"
          :sitekey="siteKey"
          size="invisible"
          @verify="onRecaptchaVerify"
          @expired="onRecaptchaExpired"
          class="por-register--recaptcha"
          data-qa="form-recaptcha" />
        </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { Auth } from '@aws-amplify/auth'
import router from '@/router'
import { mapGetters } from 'vuex'
import VueRecaptcha from 'vue-recaptcha'
import parseISO from 'date-fns/parseISO'

import { redirect } from '@/services/redirect'
import { track, identify } from '@/services/tracking'
import { getDaysInMonth, getMonthsInYear, getYears } from '@/helpers/dates'
import { isTesting } from '@/helpers/env'
import { getConsent } from '@/helpers/consent'
import consentMixin from '@/helpers/sms-consent-mixin'
import registerMixin from '@/helpers/registration-mixin'
import { errorMessageMapping } from '@/helpers/message-mapping'
import trackErrors from '@/helpers/track-errors'
import GoogleSignInHblButton from '@ui/components/google-button-hbl/GoogleSignInHblButton'
import { OAUTH_PROVIDERS } from '@/helpers/constants'
import { ssoSignIn } from '@/helpers/auth-utils'
import PhoneInput from '@/components/inputs/phone-input/PhoneInput.vue'

import Input from '@/components/inputs/input'
import Password from '@/components/inputs/password'

const SITE_KEY_E2E = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
const E2E_EMAIL_PATTERN = /^e2e\+.*@(dred|zavamed)\.com$/i

export default {
  name: 'RegisterForm',
  components: {
    Input,
    Password,
    VueRecaptcha,
    GoogleSignInHblButton,
    PhoneInput
  },
  mixins: [consentMixin, registerMixin],
  data () {
    return {
      birthdate_day: null,
      birthdate_month: null,
      birthdate_year: null,
      email: '',
      family_name: '',
      gender: '',
      given_name: '',
      googleProviderName: OAUTH_PROVIDERS.GOOGLE_PROVIDER_NAME,
      password: '',
      phone_number: '',
      phoneInputKey: 0,
      isPhoneValid: false,
      showPasswordErrorText: false,
      sms_updates: false,
      sms_offers: false,
      sms_cleared: false,
      submitStatus: null,
      terms_and_conditions: false,
      third_party_consent: false,
      smsMessagesOptedOut: false
    }
  },
  computed: {
    ...mapGetters({
      recaptchaSiteKey: 'site/recaptchaSiteKey',
      tcLink: 'site/tcLink',
      privacyLink: 'site/privacyLink',
      brand: 'site/brand',
      actualLocale: 'site/actualLocale',
      useGoogleSSO: 'site/useGoogleSSO'
    }),
    siteKey: function () {
      return !isTesting() ? this.recaptchaSiteKey : SITE_KEY_E2E
    },
    formattedBirthdate: function () {
      let formatted = `${this.birthdate_year}-${this.birthdate_month}-${this.birthdate_day}T00:00:00Z`
      let parsed = parseISO(formatted)
      let normalised = parsed.toISOString().split('T')[0]
      return normalised
    }
  },
  watch: {
    sms_updates (newVal) {
      if (newVal) {
        this.trackSmsOptionSelection('updates', 'register')
      }
    },
    sms_offers (newVal) {
      if (newVal) {
        this.trackSmsOptionSelection('offers', 'register')
      }
    },
    phone_number: function (newVal) {
      if (newVal) {
        this.smsMessagesOptedOut = false
        this.sms_cleared = false
      } else {
        this.sms_offers = false
        this.sms_updates = false

        const errors = this.$refs.registerFormValidator.errors
        const smsOptionsError = errors && errors['sms_options_selection']

        if (
          smsOptionsError &&
          smsOptionsError.length > 0 &&
          !this.smsMessagesOptedOut
        ) {
          track({
            name: 'delete',
            params: {
              module: 'register',
              description: `Phone number manually removed after no sms option error was displayed`
            }
          })
        }
      }
    }
  },
  methods: {
    getDaysInMonth,
    getMonthsInYear,
    getYears,
    async onRecaptchaVerify (data) {
      this.recaptchaSecretKey = data

      await this.registerPatient(this.recaptchaSecretKey)
    },
    onRecaptchaExpired () {
      this.recaptchaSecretKey = null
    },
    async registerPatient (recaptchaKey) {
      try {
        let email = this.validEmail
        let password = this.password
        let gender = this.gender
        let givenName = this.given_name.trim()
        let familyName = this.family_name.trim()
        let brand = this.thirdParty ? this.thirdParty : this.brand
        let consent = []
        this.$store.dispatch('user/setCredentials', { email, password, gender, given_name: givenName, family_name: familyName })
        await Auth.signOut() // In case someone already has tokens on their localStorage

        if (this.sms_offers) consent.push('sms_marketing_consent')
        if (this.third_party_consent) consent.push(getConsent(brand))

        const cognitoResponse = await Auth.signUp({
          username: email,
          password: password,
          attributes: {
            given_name: givenName,
            family_name: familyName
          },
          clientMetadata: {
            brand: this.brand,
            locale: this.actualLocale
          },
          validationData: {
            birthdate: this.formattedBirthdate,
            consent: consent.join(','),
            gender,
            notifications: this.sms_updates ? 'email_and_sms' : 'email',
            phone_number: this.phone_number,
            recaptcha_secret: recaptchaKey,
            terms_and_conditions: 'yes'
          }
        })

        const userConfirmed = cognitoResponse.userConfirmed
        track({
          name: 'register success',
          params: {
            module: 'register',
            verification_required: userConfirmed ? 'no' : 'yes'
          }
        })

        if (userConfirmed) {
          await Auth.signIn(email, password)

          let newUser = await Auth.currentSession()
          let patientId = newUser.getIdToken().decodePayload().patient_id
          let identifyPayload = {
            firstName: this.given_name,
            lastName: this.family_name,
            email: this.validEmail,
            gender: this.gender
          }

          identify(patientId, identifyPayload)

          if (window.newrelic) {
            window.newrelic.addPageAction('register')
          }

          this.submitStatus = 'OK' // state cleanup
          redirect()
        } else {
          router.push({ name: 'email-verification' })
        }
      } catch (error) {
        let errorMessage = error ? errorMessageMapping(error.message) : ''
        this.$store.dispatch('site/setMessage', { message: errorMessage, type: 'danger' })
        track({ name: 'error', params: { module: 'register', description: 'register error', error_message: errorMessage } })
        this.submitStatus = null
        this.$refs.recaptcha.reset()
      }
    },
    async submitRegister () {
      track({ name: 'button tap', params: { module: 'register', description: 'register button' } })

      this.$refs.registerFormValidator.validate().then(async success => {
        if (this.isSmsSelected) {
          this.$refs.phoneInput.updateValidation()
        }
        const phoneInputError = this.isSmsSelected ? !this.isPhoneValid : false
        const module = 'register'
        if (!success || phoneInputError) {
          this.showPasswordErrorText = true
          this.submitStatus = null
          trackErrors(this.$refs.registerFormValidator, module)
          if (phoneInputError) {
            track({ name: `register failed`, params: { description: 'phone', module } })
          }
          return
        }

        this.submitStatus = 'PENDING' // disables submit button
        // Adding the staging/development check here means the reCaptcha bypass path is actually trimmed from the production bundle.
        if ((process.env.VUE_APP_ENV === 'staging' || process.env.VUE_APP_ENV === 'development') && E2E_EMAIL_PATTERN.test(this.validEmail)) {
          this.registerPatient('no-recaptcha-e2e')
        } else {
          this.$refs.recaptcha.execute() // onRecaptchaVerify triggers register
        }
      })
    },
    async ssoSubmit (provider) {
      this.submitStatus = 'PENDING'
      track({ name: 'button tap', params: { module: 'register', description: `${provider} register button` } })
      try {
        await ssoSignIn(provider)
        this.submitStatus = 'OK'
      } catch (error) {
        this.$store.dispatch('site/setMessage', { message: error.message, type: 'danger' })
        track({ name: 'error', params: { module: 'register', error_message: error.message, description: `${provider} social sign on error for register` } })
        this.submitStatus = null
      }
    },
    updatePhoneValidity (event) {
      this.isPhoneValid = event
    },
    clearPhoneInput () {
      this.phone_number = ''
      this.sms_cleared = true
      this.sms_offers = false
      this.smsUpdates = false
      // Updating the component key to force re-render and clear the input field
      this.phoneInputKey += 1
      this.smsMessagesOptedOut = true

      track({
        name: 'button tap',
        params: {
          module: 'register',
          description: `clicked on I don’t want to receive text messages`
        }
      })
    },

    trackSmsOptionSelection (option) {
      track({
        name: 'button tap',
        params: {
          module: 'register',
          description: `SMS ${option} option selected`
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  $por-register--column-tablet-margin-top: 8px;
  $por-register--field-wrapper-margin-top: 16px;
  $por-register--recaptcha-wrapper-margin-top: 24px;

  // To match Bulma styles
  $por-register-input--border: 1px solid #D0CFCB;
  $por-register-input--border-radius: 8px;

.por-register--field-wrapper {
    display: block;
  }

  .por-register--field-wrapper__margin-top,
  .por-register--field-wrapper + .por-register--field-wrapper {
    margin-top: $por-register--field-wrapper-margin-top;
  }

  .por-register-input--wrapper-input {
    display: flex;
    align-items: baseline;
    width: 100%;
    border: $por-register-input--border;
    border-radius: $por-register-input--border-radius;
  }

  .b-checkbox.checkbox.por-register-input--wrapper-input { // Bulma override
    align-items: flex-start;
  }

  .por-register--recaptcha-wrapper {
    display: block;
    width: 100%;
    text-align: center;
  }

  .por-register--recaptcha {
    display: inline-block;
    width: auto;
    margin: $por-register--recaptcha-wrapper-margin-top auto 0 auto;
  }

  .por-register--hidden {
    display: none;
  }

  .subtitle {
    font-size: 16px;
    font-weight: 400;
    color: #4a4a4a;
  }

  @media(max-width: 768px) {
    .column + .column {
      margin-top: $por-register--column-tablet-margin-top;
    }
  }

  .por-register--field-helper-text {
    margin-top: -8px;
  }

  .por-register--sms-modal {
    background: #f5f6f9;
    border-radius: 8px;

    .por-register--sms-modal-header {
      display: flex;
      font-weight: bold;
      gap: 8px;

      &::before {
        content: url('data:image/svg+xml,<svg width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.3334 16.4168H1.66675L1.83285 16.1677C3.15905 14.1784 3.86675 12.2077 3.86675 9.81683V8.71683C3.86675 5.88177 6.16502 3.5835 9.00008 3.5835C11.8351 3.5835 14.1334 5.88177 14.1334 8.71683V9.81683C14.1334 12.2077 14.8411 14.1784 16.1673 16.1677L16.3334 16.4168Z" stroke="%230E1661" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.75 16.4165C11.75 17.9353 10.5188 19.1665 9 19.1665C7.48122 19.1665 6.25 17.9353 6.25 16.4165" stroke="%230E1661" stroke-width="1.83333"/><path d="M9 3.58333V1.75" stroke="%230E1661" stroke-width="1.83333" stroke-linecap="round"/></svg>');
        margin-top: 2px;
      }
    }

    .por-register--sms-modal-input {
      background-color: #fff;
      border: 1px solid #D0CFCB;
      border-radius: 8px;
      width: 100%;
    }
  }

  .por-register--sms-confirm {
    background-color: #F2F8F6;
    border: 2px solid #007044;
    border-radius: 8px;

    strong {
      color: #007044;
      display: flex;
      gap: 8px;

      &::before {
        content: url('data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.293 6.29297C18.6835 5.90244 19.3165 5.90244 19.707 6.29297C20.0976 6.68349 20.0976 7.31651 19.707 7.70703L9.70703 17.707C9.31651 18.0976 8.68349 18.0976 8.29297 17.707L4.29297 13.707C3.90244 13.3165 3.90244 12.6835 4.29297 12.293C4.68349 11.9024 5.31651 11.9024 5.70703 12.293L9 15.5859L18.293 6.29297Z" fill="%23007044"/></svg>');
        margin-top: 2px;
        max-height: 20px;
      }
    }
  }
</style>
